import { Grid } from '@focaldata/cin-ui-components'
import { Navigate } from 'react-router-dom'
import { validate } from 'uuid'
import ErrorPage, { ErrorType } from '../../../../controls/ErrorPage'
import { Survey, SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { SurveyIdProvider } from '../../../../hooks/SurveyIdContext'
import usePrepareApolloMiddleware from '../../../../hooks/usePrepareApolloMiddleware'
import { useProjectId } from '../../../../hooks/useProjectId'
import NavigationSubheaderLayout from '../../../../layouts/NavigationSubheaderLayout'
import { AudienceContextProvider } from '../../../../modules/Audience/Audience.context'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { Questionnaire } from '../../../../modules/Questionnaire'
import ImportFromFileDialog from '../../../../modules/Questionnaire/DocxImport/DocxImportDialog'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'
import { ActionsMenuContainer } from '../../Survey/ActionsMenu'
import { DeselectAllCardsContainer } from '../../Survey/DeselectAllCards'
import { QuestionsBulkDeleteButtonContainer } from '../../Survey/QuestionsBulkDeleteButton'
import { QuestionsSelectorCounterContainer } from '../../Survey/QuestionsSelectorCounter'
import { getMarkets, surveysByCreation } from '../markets.utils'
import { useStyles } from './MasterSurveyPage.styles'

/**
 * The shared questionnaire editor. The URL is project-scoped (no `:surveyId`), so it is
 * rendered under a SurveyIdProvider that supplies the base/master survey to every
 * `useSurveyId()` call site — the questionnaire reads/writes (project-scoped on the backend)
 * as well as read-only gating, apollo middleware and react keys.
 */
const MasterSurveyEditor = ({
  projectId,
  locked
}: {
  projectId: string
  locked: boolean
}) => {
  const { classes } = useStyles()
  usePrepareApolloMiddleware()

  // Locked once any of the scope's markets has left Draft. The step is hidden
  // then, so the only way here is a stale URL — send it back to the dashboard.
  if (locked) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  return (
    <AudienceContextProvider>
      <QuestionnaireContextProvider>
        <div className={classes.subheaderWrapper}>
          <NavigationSubheaderLayout
            rightChild={
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
              >
                <DeselectAllCardsContainer />
                <QuestionsBulkDeleteButtonContainer />
                <QuestionsSelectorCounterContainer />
                <ImportFromFileDialog />
                <ActionsMenuContainer />
              </Grid>
            }
          />
        </div>
        <div className={classes.questionaireWrapper}>
          <Questionnaire />
        </div>
      </QuestionnaireContextProvider>
    </AudienceContextProvider>
  )
}

/**
 * `markets` scopes the editor: omitted for a multi-market project, passed by a
 * wave so it targets that wave's base market and locks on that wave.
 */
export const MasterSurveyPage = ({ markets }: { markets?: Survey[] }) => {
  const projectId = useProjectId()
  const { project, loading } = useGetProjectByProjectId()
  const scopedMarkets = markets ?? getMarkets(project)

  if (!validate(projectId)) {
    return <ErrorPage errorType={ErrorType.NotFound404} />
  }

  if (loading) {
    return null
  }

  // `.at(0)` not `[0]`: noUncheckedIndexedAccess is off, so indexing would type
  // as non-optional and the guard below would read as dead code.
  const masterSurvey = surveysByCreation(scopedMarkets).at(0)
  if (!masterSurvey) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  return (
    <SurveyIdProvider value={masterSurvey.surveyId}>
      <MasterSurveyEditor
        projectId={projectId}
        locked={scopedMarkets.some(
          (market) => market.state !== SurveyState.Draft
        )}
      />
    </SurveyIdProvider>
  )
}
